import React, {useMemo, useCallback} from 'react';
import PropTypes from 'prop-types';
import {
  format,
  startOfMonth,
  endOfMonth,
  isSameDay,
  isToday,
  isPast,
  addHours,
  addDays,
  getMonth,
  getYear,
  parseISO,
  startOfDay,
} from 'date-fns';

// @mui
import {styled, alpha} from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';

// icons
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import TodayIcon from '@mui/icons-material/Today';
import PeopleIcon from '@mui/icons-material/PeopleAlt';
import BlockIcon from '@mui/icons-material/Block';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import NotInterestedIcon from '@mui/icons-material/NotInterested';

// constants
import {AVAILABILITY_STATUS, SERVICE_TYPES} from '../constants/reservation';

// ----------------------------------------------------------------------

const WEEKDAYS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

const CalendarDayStyle = styled(ButtonBase, {
  shouldForwardProp: (prop) =>
    prop !== 'isToday' &&
    prop !== 'isSelected' &&
    prop !== 'isAvailable' &&
    prop !== 'isPast' &&
    prop !== 'inMonth' &&
    prop !== 'availabilityStatus' &&
    prop !== 'hasReservations' &&
    prop !== 'isClosed' &&
    prop !== 'isException' &&
    prop !== 'isExceptionClosed' &&
    prop !== 'isImplicitExceptionClosure' &&
    prop !== 'isRegularClosure' &&
    prop !== 'exceptionService',
})(({
  theme,
  isToday,
  isSelected,
  isAvailable,
  isPast,
  inMonth,
  availabilityStatus,
  hasReservations,
  isClosed,
  isException,
  isExceptionClosed,
  isImplicitExceptionClosure,
  isRegularClosure,
  exceptionService,
}) => ({
  'width': '100%',
  'height': 40,
  'position': 'relative',
  'display': 'flex',
  'alignItems': 'center',
  'justifyContent': 'center',
  'borderRadius': '50%',
  'margin': '0 auto',
  'cursor': inMonth && isAvailable && (!isException || !isExceptionClosed) ? 'pointer' : 'default',
  'overflow': 'visible',
  'transition': theme.transitions.create(['background-color', 'border-color', 'color', 'box-shadow'], {
    duration: theme.transitions.duration.short,
  }),

  // Base styles
  ...(inMonth && {
    color: theme.palette.text.primary,
  }),
  ...(!inMonth && {
    color: 'transparent', // Rendre invisible les jours qui ne sont pas dans le mois actuel
    backgroundColor: 'transparent',
    pointerEvents: 'none',
  }),

  // Past dates - style simplifié sans fond
  ...(isPast && inMonth && {
    color: theme.palette.text.disabled,
    backgroundColor: 'transparent',
    pointerEvents: 'none',
  }),

  // Jours de fermeture normale - style simplifié sans fond
  ...(!isSelected && isRegularClosure && inMonth && !isPast && {
    color: theme.palette.text.disabled,
    backgroundColor: 'transparent',
    pointerEvents: 'none',
  }),

  // Today - toujours avec une bordure spéciale, mais la couleur dépend de la disponibilité
  ...(isToday && {
    border: isClosed ?
      `1px dashed ${theme.palette.error.light}` :
      `1px dashed ${theme.palette.primary.main}`,
    // La couleur du texte dépend de l'état
    color: isClosed ?
      theme.palette.error.main :
      theme.palette.primary.main,
    fontWeight: 'bold',
  }),

  // Availability status - only for days in current month
  ...(!isSelected && inMonth && !isException && !isRegularClosure && !isPast && {
    ...(availabilityStatus === AVAILABILITY_STATUS.AVAILABLE && {
      'backgroundColor': alpha(theme.palette.success.main, 0.12),
      'color': theme.palette.success.dark,
      '&:hover': {
        backgroundColor: alpha(theme.palette.success.main, 0.24),
      },
    }),
    ...(availabilityStatus === AVAILABILITY_STATUS.LIMITED && {
      'backgroundColor': alpha(theme.palette.warning.main, 0.12),
      'color': theme.palette.warning.dark,
      '&:hover': {
        backgroundColor: alpha(theme.palette.warning.main, 0.24),
      },
    }),
    ...(availabilityStatus === AVAILABILITY_STATUS.BUSY && {
      'backgroundColor': alpha(theme.palette.warning.main, 0.08),
      'color': theme.palette.warning.dark,
      '&:hover': {
        backgroundColor: alpha(theme.palette.warning.main, 0.16),
      },
    }),
    ...(availabilityStatus === AVAILABILITY_STATUS.UNAVAILABLE && {
      'backgroundColor': alpha(theme.palette.error.main, 0.08),
      'color': theme.palette.error.main,
      '&:hover': {
        backgroundColor: alpha(theme.palette.error.main, 0.16),
      },
    }),
    ...(availabilityStatus === AVAILABILITY_STATUS.CLOSED && {
      backgroundColor: alpha(theme.palette.error.main, 0.05),
      color: theme.palette.error.main,
      pointerEvents: 'none',
    }),
  }),

  // Exception - Style pour les exceptions (quand ils ne sont pas sélectionnés)
  ...(!isSelected && isException && inMonth && !isPast && {
    // Pour les fermetures exceptionnelles - rouge avec bordure en pointillés
    ...(isExceptionClosed && {
      'border': `2px dashed ${theme.palette.error.main}`,
      'backgroundColor': alpha(theme.palette.error.main, 0.08),
      'color': theme.palette.error.main,
      'pointerEvents': 'none',
    }),

    // Pour les exceptions d'horaires - orange avec bordure en pointillés
    ...(!isExceptionClosed && !isImplicitExceptionClosure && {
      'border': `2px dashed ${theme.palette.warning.main}`,
      'backgroundColor': alpha(theme.palette.warning.main, 0.08),
      'color': theme.palette.warning.main,
    }),

    // Pour les fermetures implicites (service non mentionné dans l'exception)
    ...(isImplicitExceptionClosure && {
      'border': `2px dashed ${theme.palette.error.main}`,
      'backgroundColor': alpha(theme.palette.error.main, 0.08),
      'color': theme.palette.error.main,
      'pointerEvents': 'none',
    }),
  }),

  // Selected - prend le dessus sur les autres styles, y compris les exceptions
  ...(isSelected && {
    'backgroundColor': theme.palette.primary.main,
    'color': theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.primary.dark,
    },
    'boxShadow': `0 0 0 2px ${alpha(theme.palette.primary.main, 0.24)}`,
    // Style pour l'indicateur d'exception quand c'est sélectionné
    ...(isException && {
      // Garder un rappel visuel que c'est une exception
      'border': isExceptionClosed ?
        `2px solid ${theme.palette.error.main}` :
        `2px solid ${theme.palette.warning.main}`,
    }),
  }),

  // Hover - only for days in current month
  '&:hover': {
    ...(isAvailable && !isSelected && inMonth && (!isException || !isExceptionClosed) && {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    }),
  },

  // Extra animation for days with reservations
  ...(hasReservations && inMonth && !isException && {
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 2,
      left: '50%',
      transform: 'translateX(-50%)',
      width: 4,
      height: 4,
      borderRadius: '50%',
      backgroundColor: theme.palette.info.main,
    },
  }),
}));

// ----------------------------------------------------------------------

function DayCell({
  day,
  inMonth,
  availabilityMap,
  selectedDate,
  onDateSelect,
  bookingConstraints,
  exceptions,
  selectedService,
  serviceSchedule,
}) {
  const dateKey = format(day, 'yyyy-MM-dd');
  const availability = availabilityMap[dateKey];

  // Fonction complètement réécrite pour gérer tous les formats possibles de dates
  const isDateInRange = useCallback((dateToCheck, startDateRaw, endDateRaw) => {
    // Fonction utilitaire pour convertir n'importe quel format de date en date sans heure
    const normalizeDate = (dateInput) => {
      let dateObj;

      // Si c'est un objet avec une propriété $date (format MongoDB)
      if (dateInput && typeof dateInput === 'object' && dateInput.$date) {
        dateObj = new Date(dateInput.$date);
        // Si c'est déjà une instance de Date
      } else if (dateInput instanceof Date) {
        dateObj = dateInput;
        // Si c'est une chaîne ISO
      } else if (typeof dateInput === 'string') {
        try {
          dateObj = parseISO(dateInput);
        } catch (e) {
          // Fallback à l'ancien constructeur Date
          dateObj = new Date(dateInput);
        }
        // Par défaut, essayer de construire une Date
      } else {
        dateObj = new Date(dateInput);
      }

      // Retourner uniquement la date (sans l'heure)
      return startOfDay(dateObj);
    };

    // Normaliser les dates pour la comparaison pure des jours (sans les heures)
    const dateDay = startOfDay(dateToCheck);
    const startDay = normalizeDate(startDateRaw);
    const endDay = normalizeDate(endDateRaw);

    // Convertir en timestamps pour comparaison numérique
    const dateDayTime = dateDay.getTime();
    const startDayTime = startDay.getTime();
    const endDayTime = endDay.getTime();

    return dateDayTime >= startDayTime && dateDayTime <= endDayTime;
  }, []);

  // Fonction pour vérifier si un service est normalement fermé pour ce jour
  // Utiliser useCallback pour éviter les rerenders inutiles
  const isServiceNormallyClosedForDay = useCallback((service) => {
    if (!serviceSchedule) return true;

    const dayOfWeek = format(day, 'EEEE').toLowerCase();

    // Si pas de planning pour ce jour du tout, tous les services sont fermés
    if (!serviceSchedule[dayOfWeek] || serviceSchedule[dayOfWeek].length === 0) {
      return true;
    }

    // Vérifier si le service spécifique existe pour ce jour
    return !serviceSchedule[dayOfWeek].some((s) => s.name === service && s.active);
  }, [day, serviceSchedule]);

  // Vérifier si ce jour est normalement fermé pour tous les services
  const isDayCompletelyClosedNormally = useCallback(() => {
    if (!serviceSchedule) return true;

    const dayOfWeek = format(day, 'EEEE').toLowerCase();

    // Si pas de planning pour ce jour ou si le planning est vide, le jour est complètement fermé
    return !serviceSchedule[dayOfWeek] || serviceSchedule[dayOfWeek].length === 0 ||
           !serviceSchedule[dayOfWeek].some((s) => s.active);
  }, [day, serviceSchedule]);

  // Fonction pour déterminer quels services sont affectés par une exception
  const getAffectedServices = useCallback((exception) => {
    // Si l'exception est une fermeture complète, tous les services sont affectés
    if (exception.isClosed) {
      return {
        all: true,
        lunch: {affected: true, explicit: false, closed: true},
        dinner: {affected: true, explicit: false, closed: true},
      };
    }

    // Si l'exception a des services spécifiques
    if (exception.services && exception.services.length > 0) {
      const hasLunch = exception.services.some((s) => s.name === SERVICE_TYPES.LUNCH);
      const hasDinner = exception.services.some((s) => s.name === SERVICE_TYPES.DINNER);

      return {
        all: false,
        lunch: {
          affected: true,
          explicit: hasLunch, // Si midi est explicitement mentionné
          closed: !hasLunch, // Si midi n'est pas mentionné, considéré fermé
        },
        dinner: {
          affected: true,
          explicit: hasDinner, // Si soir est explicitement mentionné
          closed: !hasDinner, // Si soir n'est pas mentionné, considéré fermé
        },
      };
    }

    // Si l'exception a seulement des horaires d'ouverture générales
    if (exception.openTime && exception.closeTime) {
      return {
        all: false,
        lunch: {affected: true, explicit: true, closed: false},
        dinner: {affected: true, explicit: true, closed: false},
      };
    }

    // Par défaut
    return {
      all: false,
      lunch: {affected: true, explicit: false, closed: false},
      dinner: {affected: true, explicit: false, closed: false},
    };
  }, []);

  // Vérifier si le jour est dans une exception et déterminer quel service est affecté
  const exceptionInfo = useMemo(() => {
    if (!exceptions || !Array.isArray(exceptions) || exceptions.length === 0) {
      return null;
    }

    // Chercher dans les exceptions
    for (const exception of exceptions) {
      // Exception de type range
      if (exception.type === 'range' && exception.range) {
        // Utiliser la fonction améliorée isDateInRange
        if (isDateInRange(day, exception.range.start, exception.range.end)) {
          // Analyser les services affectés
          const affectedServices = getAffectedServices(exception);

          return {
            name: exception.name,
            isClosed: exception.isClosed,
            affectedServices,
            servicesInfo: exception.services || [],
          };
        }
      }

      // Exception de type unique (date spécifique)
      if (exception.type === 'unique' && exception.date) {
        const exceptionDate = typeof exception.date === 'object' && exception.date.$date ?
          new Date(exception.date.$date) :
          new Date(exception.date);

        if (isSameDay(day, exceptionDate)) {
          // Analyser les services affectés
          const affectedServices = getAffectedServices(exception);

          return {
            name: exception.name,
            isClosed: exception.isClosed,
            affectedServices,
            servicesInfo: exception.services || [],
          };
        }
      }

      // Exception récurrente (jour de la semaine)
      if (exception.type === 'recurring' && exception.dayOfWeek) {
        const dayOfWeek = format(day, 'EEEE').toLowerCase();
        if (dayOfWeek === exception.dayOfWeek) {
          // Analyser les services affectés
          const affectedServices = getAffectedServices(exception);

          return {
            name: exception.name,
            isClosed: exception.isClosed,
            affectedServices,
            servicesInfo: exception.services || [],
          };
        }
      }
    }

    return null;
  }, [day, exceptions, getAffectedServices, isDateInRange]);

  // Déterminer le service affecté par l'exception en fonction du service sélectionné
  const exceptionServiceInfo = useMemo(() => {
    if (!exceptionInfo || !exceptionInfo.affectedServices) {
      return {type: 'none', explicit: false, closed: false};
    }

    const services = exceptionInfo.affectedServices;

    // Si tous les services sont affectés
    if (services.all) {
      return {type: 'all', explicit: false, closed: true};
    }

    // Si le service sélectionné est midi
    if (selectedService === SERVICE_TYPES.LUNCH) {
      if (services.lunch.affected) {
        return {
          type: 'lunch',
          explicit: services.lunch.explicit,
          closed: services.lunch.closed,
        };
      }
      // Si le service sélectionné est soir
    } else if (selectedService === SERVICE_TYPES.DINNER) {
      if (services.dinner.affected) {
        return {
          type: 'dinner',
          explicit: services.dinner.explicit,
          closed: services.dinner.closed,
        };
      }
      // Si aucun service n'est sélectionné, montrer l'exception pertinente
    } else {
      // Si l'un des services est explicitement mentionné
      if (services.lunch.explicit || services.dinner.explicit) {
        if (services.lunch.explicit) {
          return {type: 'lunch', explicit: true, closed: false};
        } else {
          return {type: 'dinner', explicit: true, closed: false};
        }
      } else {
        // Si aucun service n'est explicitement mentionné
        return {type: 'all', explicit: false, closed: true};
      }
    }

    return {type: 'none', explicit: false, closed: false};
  }, [exceptionInfo, selectedService]);

  // Déterminer si l'exception a un effet positif (ouvre un jour normalement fermé)
  const exceptionHasPositiveEffect = useMemo(() => {
    if (!exceptionInfo || !exceptionInfo.affectedServices) return false;

    const services = exceptionInfo.affectedServices;

    // Si au moins un des services spécifiés dans l'exception est normalement fermé ce jour-là
    // mais ouvert grâce à l'exception, c'est un effet positif
    if (services.lunch && services.lunch.explicit && isServiceNormallyClosedForDay(SERVICE_TYPES.LUNCH)) {
      return true;
    }

    if (services.dinner && services.dinner.explicit && isServiceNormallyClosedForDay(SERVICE_TYPES.DINNER)) {
      return true;
    }

    return false;
  }, [exceptionInfo, isServiceNormallyClosedForDay]);

  // Définir si c'est une fermeture implicite par exception (quand un service n'est pas mentionné dans l'exception)
  const isImplicitExceptionClosure = useMemo(() => {
    return exceptionInfo &&
           exceptionServiceInfo.type !== 'none' &&
           !exceptionInfo.isClosed &&
           exceptionServiceInfo.closed;
  }, [exceptionInfo, exceptionServiceInfo]);

  // Définition améliorée de shouldShowAsException
  const shouldShowAsException = useMemo(() => {
    // Pas d'exception, on s'arrête là
    if (!exceptionInfo || exceptionServiceInfo.type === 'none') return false;

    // Si c'est une fermeture complète, toujours l'afficher
    if (exceptionInfo.isClosed) return true;

    // Si un service spécifique est sélectionné
    if (selectedService) {
      // Si c'est une fermeture implicite d'un service normalement ouvert
      if (isImplicitExceptionClosure && !isServiceNormallyClosedForDay(selectedService)) return true;

      // Si c'est une ouverture exceptionnelle d'un service normalement fermé
      if (exceptionServiceInfo.explicit && isServiceNormallyClosedForDay(selectedService)) return true;

      // Si le service est explicitement mentionné dans l'exception
      if (exceptionServiceInfo.explicit) return true;
    } else {
      // Si aucun service n'est sélectionné

      // Si l'exception ouvre un service normalement fermé
      if (exceptionHasPositiveEffect) return true;

      // Si l'exception ferme implicitement un service normalement ouvert
      if (isImplicitExceptionClosure) {
        return !isServiceNormallyClosedForDay(SERVICE_TYPES.LUNCH) ||
               !isServiceNormallyClosedForDay(SERVICE_TYPES.DINNER);
      }
    }

    // Par défaut, ne pas montrer comme exception
    return false;
  }, [
    exceptionInfo,
    exceptionServiceInfo,
    selectedService,
    isImplicitExceptionClosure,
    isServiceNormallyClosedForDay,
    exceptionHasPositiveEffect,
  ]);

  // Vérifier si c'est un jour de fermeture normale du restaurant (sans exception)
  const isRegularClosure = useMemo(() => {
    return !shouldShowAsException &&
      (selectedService ? isServiceNormallyClosedForDay(selectedService) : isDayCompletelyClosedNormally());
  }, [shouldShowAsException, selectedService, isServiceNormallyClosedForDay, isDayCompletelyClosedNormally]);

  // Extraire le type de service concerné par l'exception
  const exceptionService = exceptionServiceInfo.type;

  // Memoize date status for optimization
  const {
    isAvailable,
    isDateToday,
    isPastDate,
    isSelected,
    availabilityStatus,
    tooltipText,
    hasReservations,
    isClosed,
    todayStatusReason,
  } = useMemo(() => {
    const now = new Date();
    const minimumBookingTime = addHours(now, bookingConstraints.minimumNoticeHours);
    const maximumBookingDate = addDays(now, bookingConstraints.advanceBookingDays);

    const isDateToday = isToday(day);
    const isPastDate = isPast(day) && !isDateToday;
    const isSelected = selectedDate && isSameDay(day, selectedDate);

    // Pour aujourd'hui, nous nous fions aux données d'API plutôt qu'à notre calcul local
    const isWithinBookingWindow = isDateToday ?
      (availability?.status === AVAILABILITY_STATUS.AVAILABLE || !!availability) :
      // Pour les jours futurs, ils sont disponibles s'ils sont avant la date maximale
      // et qu'ils ne sont pas dans le passé
      !isPastDate && day <= maximumBookingDate;

    // Determine availability status
    let status = AVAILABILITY_STATUS.CLOSED;
    let isClosed = true; // Par défaut considéré comme fermé
    let todayStatusReason = ''; // Raison du statut pour aujourd'hui

    // Default - no reservations
    let hasReservations = false;

    // Si c'est un jour fermé régulièrement (hors exception), on le marque comme fermé
    if (isRegularClosure) {
      status = AVAILABILITY_STATUS.CLOSED;
      isClosed = true;
      todayStatusReason = 'regular_closure';
      // Si c'est une exception et que l'établissement est fermé, ce jour n'est pas disponible
    } else if (shouldShowAsException && (exceptionInfo?.isClosed || isImplicitExceptionClosure)) {
      status = AVAILABILITY_STATUS.CLOSED;
      isClosed = true;
      todayStatusReason = 'exception';
    } else if (isWithinBookingWindow && !isPastDate && (!shouldShowAsException || (shouldShowAsException && !exceptionInfo?.isClosed && !isImplicitExceptionClosure))) {
      if (availability?.status === AVAILABILITY_STATUS.AVAILABLE || (isDateToday && !!availability)) {
        status = AVAILABILITY_STATUS.AVAILABLE;
        isClosed = false;
        // Verify if the day has some reservations but is still available
        if (availability?.hasReservations) {
          hasReservations = true;
        }
      } else if (availability?.status === AVAILABILITY_STATUS.LIMITED) {
        status = AVAILABILITY_STATUS.LIMITED;
        isClosed = false;
        hasReservations = true;
      } else if (availability?.status === AVAILABILITY_STATUS.BUSY) {
        status = AVAILABILITY_STATUS.BUSY;
        isClosed = false;
        hasReservations = true;
      } else if (inMonth) {
        status = AVAILABILITY_STATUS.UNAVAILABLE;
        isClosed = true;

        // Déterminer la raison du statut pour aujourd'hui
        if (isDateToday) {
          if (day < minimumBookingTime) {
            todayStatusReason = 'minimum_notice'; // Hors délai minimum
          } else if (availability?.fullyBooked) {
            todayStatusReason = 'fully_booked'; // Complet
          } else {
            todayStatusReason = 'unavailable'; // Non disponible pour une autre raison
          }
        }
      }
    }

    // Determine tooltip text
    let tooltip = '';

    // Priorité au message d'exception
    if (shouldShowAsException) {
      // Message simplifié pour les exceptions
      if (exceptionInfo.isClosed) {
        tooltip = `Fermé : ${exceptionInfo.name}`;
      } else if (isImplicitExceptionClosure) {
        // Vérifier si le service est normalement fermé ce jour-là
        if (!isServiceNormallyClosedForDay(selectedService)) {
          tooltip = `${selectedService === SERVICE_TYPES.LUNCH ? 'Midi' : 'Soir'} : fermé exceptionnellement - ${exceptionInfo.name}`;
        } else {
          // Si le service est normalement fermé, utiliser le message standard de fermeture
          tooltip = `Pas de service "${selectedService === SERVICE_TYPES.LUNCH ? 'Midi' : 'Soir'}" ce jour-là`;
        }
      } else {
        // Récupérer d'abord les détails des horaires modifiés s'ils existent
        let horaireDetails = '';
        if (exceptionInfo.servicesInfo && exceptionInfo.servicesInfo.length > 0) {
          const servicesDetails = exceptionInfo.servicesInfo.map(
              (s) => `${s.name === SERVICE_TYPES.LUNCH ? 'Midi' : 'Soir'}: ${s.start}-${s.end}`,
          ).join(', ');
          horaireDetails = ` (${servicesDetails})`;
        }

        // Ajouter les horaires modifiés avant la raison
        tooltip = `Horaires modifiés${horaireDetails} : ${exceptionInfo.name}`;
      }
      // Message pour les jours de fermeture normale
    } else if (isRegularClosure) {
      // Si aucun service n'est défini ce jour-là
      const dayOfWeek = format(day, 'EEEE').toLowerCase();
      const hasAnyService = serviceSchedule && serviceSchedule[dayOfWeek] && serviceSchedule[dayOfWeek].length > 0 &&
                          serviceSchedule[dayOfWeek].some((s) => s.active);

      if (!hasAnyService) {
        tooltip = 'Fermé ce jour-là';
      } else if (selectedService) {
        tooltip = `Pas de service "${selectedService === SERVICE_TYPES.LUNCH ? 'Midi' : 'Soir'}" ce jour-là`;
      } else {
        tooltip = 'Certains services non disponibles ce jour-là';
      }
    } else if (isDateToday) {
      if (isClosed) {
        // Messages spécifiques selon la raison de l'indisponibilité
        if (todayStatusReason === 'minimum_notice') {
          tooltip = `Aujourd'hui (hors délai - min. ${bookingConstraints.minimumNoticeHours}h à l'avance)`;
        } else if (todayStatusReason === 'fully_booked') {
          tooltip = 'Aujourd\'hui (complet)';
        } else if (todayStatusReason === 'exception') {
          tooltip = `Aujourd'hui (fermé - ${exceptionInfo?.name || 'exception'})`;
        } else {
          tooltip = 'Aujourd\'hui (plus de disponibilités)';
        }
      } else {
        tooltip = 'Aujourd\'hui';
      }
    } else if (isPastDate) {
      tooltip = 'Date passée';
    } else if (!isWithinBookingWindow) {
      if (day < minimumBookingTime) {
        tooltip = `Réservation possible au minimum ${bookingConstraints.minimumNoticeHours}h à l'avance`;
      } else {
        tooltip = `Réservation possible jusqu'à ${bookingConstraints.advanceBookingDays} jours à l'avance`;
      }
    } else {
      switch (status) {
        case AVAILABILITY_STATUS.AVAILABLE:
          tooltip = hasReservations ? 'Disponible (certains créneaux déjà réservés)' : 'Disponible';
          break;
        case AVAILABILITY_STATUS.LIMITED:
          tooltip = 'Disponibilité limitée';
          break;
        case AVAILABILITY_STATUS.BUSY:
          tooltip = 'Peu de disponibilités';
          break;
        case AVAILABILITY_STATUS.UNAVAILABLE:
          tooltip = 'Indisponible';
          break;
        case AVAILABILITY_STATUS.CLOSED:
          tooltip = 'Fermé';
          break;
        default:
          tooltip = '';
      }
    }

    return {
      isAvailable: status === AVAILABILITY_STATUS.AVAILABLE || status === AVAILABILITY_STATUS.LIMITED || status === AVAILABILITY_STATUS.BUSY,
      isDateToday,
      isPastDate,
      isSelected,
      availabilityStatus: status,
      tooltipText: tooltip,
      hasReservations,
      isClosed,
      todayStatusReason,
    };
  }, [
    day,
    inMonth,
    availability,
    selectedDate,
    bookingConstraints,
    exceptionInfo,
    shouldShowAsException,
    isImplicitExceptionClosure,
    isRegularClosure,
    selectedService,
    serviceSchedule,
    isServiceNormallyClosedForDay,
  ]);

  // Handle day click
  const handleClick = () => {
    if (isAvailable && inMonth && (!shouldShowAsException || !exceptionInfo?.isClosed) && onDateSelect) {
      onDateSelect(day);
    }
  };

  // Icons to display
  const renderDayIcon = () => {
    if (shouldShowAsException) {
      return (
        <Box
          sx={{
            position: 'absolute',
            top: -5,
            right: -5,
            bgcolor: 'background.paper',
            borderRadius: '50%',
            width: 16,
            height: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {exceptionInfo.isClosed || isImplicitExceptionClosure ? (
            <NotInterestedIcon sx={{fontSize: 12, color: 'error.main'}} />
          ) : (
            <EventAvailableIcon sx={{fontSize: 12, color: 'warning.main'}} />
          )}
        </Box>
      );
    }

    if (isDateToday) {
      return (
        <Box
          sx={{
            position: 'absolute',
            top: -5,
            right: -5,
            bgcolor: 'background.paper',
            borderRadius: '50%',
            width: 16,
            height: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isClosed ? (
            todayStatusReason === 'minimum_notice' ? (
              // Icône pour "hors délai"
              <AccessTimeIcon sx={{fontSize: 12, color: 'text.secondary'}} />
            ) : todayStatusReason === 'fully_booked' ? (
              // Icône pour "complet"
              <EventBusyIcon sx={{fontSize: 12, color: 'warning.main'}} />
            ) : todayStatusReason === 'exception' ? (
              // Icône pour "fermé par exception"
              <NotInterestedIcon sx={{fontSize: 12, color: 'error.main'}} />
            ) : (
              // Icône par défaut pour "indisponible"
              <BlockIcon sx={{fontSize: 12, color: 'error.main'}} />
            )
          ) : (
            <TodayIcon sx={{fontSize: 12, color: 'primary.main'}} />
          )}
        </Box>
      );
    }

    if (isAvailable && hasReservations) {
      return (
        <Box
          sx={{
            position: 'absolute',
            top: -5,
            right: -5,
            bgcolor: 'background.paper',
            borderRadius: '50%',
            width: 16,
            height: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PeopleIcon sx={{fontSize: 12, color: 'info.main'}} />
        </Box>
      );
    }

    if (isAvailable) {
      return (
        <Box
          sx={{
            position: 'absolute',
            top: -5,
            right: -5,
            bgcolor: 'background.paper',
            borderRadius: '50%',
            width: 16,
            height: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <EventAvailableIcon sx={{fontSize: 12, color: 'success.main'}} />
        </Box>
      );
    }

    // Pas d'icône pour les jours de fermeture habituelle ou dates passées
    return null;
  };

  // Si le jour n'est pas dans le mois actuel, renvoyer une cellule vide
  if (!inMonth) {
    return (
      <Box sx={{
        position: 'relative',
        width: 40,
        height: 40,
        mx: 'auto',
        my: 0.5,
      }}>
        <CalendarDayStyle
          inMonth={inMonth}
        >
          {format(day, 'd')}
        </CalendarDayStyle>
      </Box>
    );
  }

  return (
    <Tooltip
      title={tooltipText}
      arrow
      placement="top"
    >
      <Box sx={{
        position: 'relative',
        width: 40,
        height: 40,
        mx: 'auto',
        my: 0.5,
      }}>
        <CalendarDayStyle
          onClick={handleClick}
          isToday={isDateToday}
          isSelected={isSelected}
          isAvailable={isAvailable}
          isPast={isPastDate}
          inMonth={inMonth}
          availabilityStatus={availabilityStatus}
          hasReservations={hasReservations}
          isClosed={isClosed}
          isException={shouldShowAsException}
          isExceptionClosed={shouldShowAsException && exceptionInfo?.isClosed}
          isImplicitExceptionClosure={isImplicitExceptionClosure}
          isRegularClosure={isRegularClosure}
          exceptionService={exceptionService}
          disabled={!isAvailable || (shouldShowAsException && exceptionInfo?.isClosed)}
        >
          {format(day, 'd')}
        </CalendarDayStyle>
        {renderDayIcon()}
      </Box>
    </Tooltip>
  );
}

DayCell.propTypes = {
  day: PropTypes.instanceOf(Date).isRequired,
  inMonth: PropTypes.bool.isRequired,
  availabilityMap: PropTypes.object.isRequired,
  selectedDate: PropTypes.instanceOf(Date),
  onDateSelect: PropTypes.func.isRequired,
  bookingConstraints: PropTypes.object.isRequired,
  exceptions: PropTypes.array,
  selectedService: PropTypes.string,
  serviceSchedule: PropTypes.object,
};

DayCell.defaultProps = {
  exceptions: [],
  selectedService: null,
  serviceSchedule: {},
};

// ----------------------------------------------------------------------

export function ReservationCalendar({
  currentMonth,
  availabilityMap,
  selectedDate,
  onDateSelect,
  bookingConstraints,
  exceptions,
  selectedService,
  serviceSchedule,
}) {
  // Generate calendar days
  const calendarDays = useMemo(() => {
    // Get first day of the month (to find offset)
    const firstDayOfMonth = startOfMonth(currentMonth);
    const lastDayOfMonth = endOfMonth(currentMonth);

    // Get day of week of first day (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    // Convert to Monday-based index (0 = Monday, ..., 6 = Sunday)
    const firstDayIndex = (firstDayOfMonth.getDay() + 6) % 7;

    // Calculate total days to show (includes days from previous/next months)
    const totalDays = 7 * Math.ceil((lastDayOfMonth.getDate() + firstDayIndex) / 7);

    // Generate array of dates
    const startDate = new Date(firstDayOfMonth);
    startDate.setDate(startDate.getDate() - firstDayIndex);

    // Create array of all days to display
    return Array.from({length: totalDays}, (_, i) => {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);

      // Un jour est dans le mois actuel si son mois et son année correspondent
      const inMonth = getMonth(date) === getMonth(currentMonth) &&
                       getYear(date) === getYear(currentMonth);

      return {
        date,
        inMonth,
      };
    });
  }, [currentMonth]);

  return (
    <Box>
      {/* Weekday headers */}
      <Grid container spacing={0} sx={{mb: 1}}>
        {WEEKDAYS.map((day, index) => (
          <Grid item xs={12/7} key={index}>
            <Typography
              align="center"
              variant="subtitle2"
              color="text.secondary"
              sx={{fontWeight: 'bold'}}
            >
              {day}
            </Typography>
          </Grid>
        ))}
      </Grid>

      {/* Calendar days */}
      <Grid container spacing={0}>
        {calendarDays.map(({date, inMonth}, index) => (
          <Grid item xs={12/7} key={index}>
            <DayCell
              day={date}
              inMonth={inMonth}
              availabilityMap={availabilityMap}
              selectedDate={selectedDate}
              onDateSelect={onDateSelect}
              bookingConstraints={bookingConstraints}
              exceptions={exceptions}
              selectedService={selectedService}
              serviceSchedule={serviceSchedule}
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

ReservationCalendar.propTypes = {
  currentMonth: PropTypes.instanceOf(Date).isRequired,
  availabilityMap: PropTypes.object.isRequired,
  selectedDate: PropTypes.instanceOf(Date),
  onDateSelect: PropTypes.func.isRequired,
  bookingConstraints: PropTypes.object.isRequired,
  exceptions: PropTypes.array,
  selectedService: PropTypes.string,
  serviceSchedule: PropTypes.object,
};

ReservationCalendar.defaultProps = {
  exceptions: [],
  selectedService: null,
  serviceSchedule: {},
};
